import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { StatusDot } from "@/components/StatusDot";
import { api, type OpsHealth } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Live control-plane health pill for the topbar — polls the worker/queue
 *  health endpoint so degraded states are visible from every page. */
export function HealthPill() {
  const { data } = useQuery({
    queryKey: ["ops-health"],
    queryFn: () => api.get<OpsHealth>("/api/ops/health"),
    refetchInterval: 10_000,
  });
  const status = data?.status ?? "ok";
  const label = !data ? "health…" : status === "ok" ? "systems normal" : status;
  return (
    <Link
      to="/ops"
      title={data?.reasons?.join("\n") || "control-plane health"}
      className={cn(
        "flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        status === "ok" && "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15",
        status === "degraded" && "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/15",
        status === "down" && "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/15",
      )}
    >
      <StatusDot status={status} pulse={status !== "ok"} />
      {label}
      {data && data.queue.failed_24h > 0 && (
        <span className="rounded-full bg-red-500/20 px-1.5 text-[10px] num">
          {data.queue.failed_24h}
        </span>
      )}
    </Link>
  );
}
